import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import UserAction from "../actions/UserAction";
import ProjectAction from "../actions/ProjectAction";
import { fromJS } from "immutable";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import { MagnifyingGlassIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import GridTable from "../grid-v2/GridTable";
import ConfirmDialog from "../dialog/ConfirmDialog";
import OctagonAlertIcon from "../icons/OctagonAlertIcon";
import TriangleAlertIcon from "../icons/TriangleAlertIcon";
import { SelectField } from "../components/ui";
import { PlusCircleIcon } from "lucide-react";
import Avatar from "../components/Avatar";
import { searchUsers } from "../api/UserApi";

const MemberSetting = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { projectId }: any = useParams();

  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const accountId = currentUser.get("accountId");
  const project = useSelector((state: any) => state.projectReducer.get("project"));
  const projectMembers = project.get("members") || fromJS([]);

  const projectMemberIds = useMemo(() => {
    return projectMembers.toJS().map((item: any) => item.accountId) || [];
  }, [projectMembers]);

  const [roleList, setRoleList] = useState<any[]>([]);
  const [role, setRole] = useState<boolean>(undefined);
  const [statusList, setStatusList] = useState<any[]>([]);
  const [status, setStatus] = useState<boolean>(undefined);

  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>(searchText);
  const [teamMemberList, setTeamMemberList] = useState<any[]>([]);
  const [addMemberSearch, setAddMemberSearch] = useState<string>("");
  const [debouncedAddMemberSearch, setDebouncedAddMemberSearch] = useState<string>("");

  const tmMap = useMemo(() => {
    const map: Record<string, { label?: string; avatarUrl?: string }> = {};
    teamMemberList.forEach((u: any) => {
      if (u?.value) map[u.value] = { label: u.label, avatarUrl: u.avatarUrl };
    });
    return map;
  }, [teamMemberList]);

  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });

  const memberHeaders = [
    { key: "user", title: "user" },
    { key: "role", title: "role" },
    { key: "status", title: "status" },
    { key: "action", title: "" },
  ];

  const availableMembers = useMemo(() => {
    return teamMemberList.filter((member: any) => !projectMemberIds.includes(member.value));
  }, [teamMemberList, projectMemberIds]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 800);
    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedAddMemberSearch(addMemberSearch);
    }, 800);
    return () => clearTimeout(handler);
  }, [addMemberSearch]);

  useEffect(() => {
    const loadTeamMembers = async () => {
      try {
        const users = await searchUsers(debouncedAddMemberSearch);
        setTeamMemberList(
          users.map((item: any) => ({
            value: item.accountId,
            label: item.displayName,
            avatarUrl: item.avatarUrl,
          }))
        );
      } catch (error) {
        console.error("Error loading team members:", error);
      }
    };
    loadTeamMembers();
  }, [debouncedAddMemberSearch]);

  const getStatus = (active: boolean) => {
    return (
      <div
        className={`capitalize w-max rounded-full border border-gray-200 py-1 px-2 ${
          active ? "text-white bg-blue-600" : "text-white bg-red-500"
        }`}
      >
        {active ? t("Active") : t("Inactive")}
      </div>
    );
  };

  useEffect(() => {
    const roleDatas = [];
    const statusDatas = [];
    projectMembers.toJS().map((item) => {
      if (!roleDatas.some((obj) => obj.value === item.admin)) {
        roleDatas.push({
          value: item.admin,
          label: item.admin ? t("Admin") : t("User"),
        });
      }
      if (!statusDatas.some((obj) => obj.value === item.active)) {
        statusDatas.push({
          value: item.active,
          label: getStatus(item.active),
        });
      }
    });
    setRoleList(roleDatas);
    setStatusList(statusDatas);
  }, [projectMembers]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEnableAdmin = (userId, admin) => {
    setShowConfirm({
      show: true,
      title: admin ? t("Make User") : t("Make Admin"),
      description: t("Are you sure you want to make {{text}} this member?", {
        text: admin ? t("user") : t("admin"),
      }),
      type: "ENABLE_ADMIN",
      data: userId,
    });
  };

  const handleActiveUser = (userId, active) => {
    setShowConfirm({
      show: true,
      title: active ? t("Deactivate Member") : t("Activate Member"),
      description: t("Are you sure you want to {{text}} this member?", {
        text: active ? t("deactivate") : t("activate"),
      }),
      type: "ACTIVE_USER",
      data: userId,
    });
  };

  const handleRemoveUser = (userId) => {
    setShowConfirm({
      show: true,
      title: t("Remove Member"),
      description: t("Are you sure you want to remove this member?"),
      alertIcon: <TriangleAlertIcon />,
      dangerBtn: true,
      type: "REMOVE_USER",
      data: userId,
    });
  };

  const filterData = useMemo(() => {
    const getDisplayName = (item: any) =>
      item.displayName ||
      item.name ||
      item.fullName ||
      item.label ||
      tmMap[item.accountId]?.label ||
      item.email ||
      t("Unknown");

    const getAvatar = (item: any) => item.avatarUrl || tmMap[item.accountId]?.avatarUrl || "";

    let datas = projectMembers
      .toJS()
      .sort((a, b) => (getDisplayName(a) || "").localeCompare(getDisplayName(b) || ""));

    if (debouncedSearchText) {
      const q = debouncedSearchText.trim().toLowerCase();
      datas = datas.filter((item) => {
        const display = (getDisplayName(item) || "").toLowerCase();
        const email = (item.email || "").toLowerCase();
        return display.includes(q) || email.includes(q);
      });
    }

    if (role !== null && role !== undefined) {
      datas = datas.filter((item) => item.admin === role);
    }
    if (status !== null && status !== undefined) {
      datas = datas.filter((item) => item.active === status);
    }

    return datas.map((item) => {
      const display = getDisplayName(item);
      const avatar = getAvatar(item);
      return {
        user: (
          <div className="flex items-center min-w-0 gap-x-2">
            <img className="h-8 w-8 flex-none rounded-full bg-gray-50" src={avatar} alt="" />
            <div className="min-w-0 flex-auto">
              <p className="text-sm font-semibold leading-6 text-gray-900">
                {display}
                {item.email ? ` (${item.email})` : ""}
              </p>
            </div>
          </div>
        ),
        role: item.admin ? t("Admin") : t("User"),
        status: getStatus(item.active),
        action:
          item.accountId !== accountId ? (
            <Menu as="div" className="relative flex-none w-max">
              <MenuButton className="block p-2.5 text-gray-500 hover:text-gray-900">
                <EllipsisHorizontalIcon aria-hidden="true" className="size-5" />
              </MenuButton>
              <MenuItems
                transition
                anchor="bottom start"
                className="absolute right-0 z-10 mt-2 w-max origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
              >
                <MenuItem>
                  <div
                    onClick={() => {
                      handleActiveUser(item.accountId, item.active);
                    }}
                    className="block px-3 py-1 text-sm/6 text-gray-900 data-focus:bg-gray-50 data-focus:outline-hidden"
                  >
                    {item.active ? t("Deactivate") : t("Activate")}
                  </div>
                </MenuItem>
                <MenuItem>
                  <div
                    onClick={() => {
                      handleEnableAdmin(item.accountId, item.admin);
                    }}
                    className="block px-3 py-1 text-sm/6 text-gray-900 data-focus:bg-gray-50 data-focus:outline-hidden"
                  >
                    {item.admin ? t("Make User") : t("Make Admin")}
                  </div>
                </MenuItem>
                <MenuItem>
                  <div
                    onClick={() => {
                      handleRemoveUser(item.accountId);
                    }}
                    className="block px-3 py-1 text-sm/6 text-red-600 data-focus:bg-gray-50 data-focus:outline-hidden"
                  >
                    {t("Remove")}
                  </div>
                </MenuItem>
              </MenuItems>
            </Menu>
          ) : null,
      };
    });
  }, [projectMembers, debouncedSearchText, role, status, tmMap]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (showConfirm.type === "ENABLE_ADMIN") {
      dispatch(UserAction.enableAdmin(showConfirm.data)).then(() => {
        dispatch(ProjectAction.getProject(projectId));
      });
    } else if (showConfirm.type === "ACTIVE_USER") {
      dispatch(UserAction.activeMember(showConfirm.data)).then(() => {
        dispatch(ProjectAction.getProject(projectId));
      });
    } else if (showConfirm.type === "REMOVE_USER") {
      dispatch(UserAction.removeMember(showConfirm.data)).then(() => {
        dispatch(ProjectAction.getProject(projectId));
      });
    } else if (showConfirm.type === "ADD_MEMBER") {
      const newMember = teamMemberList.find((member) => member.value === showConfirm.data);
      if (newMember) {
        const memberData = {
          accountId: newMember.value,
          displayName: newMember.label,
          avatarUrl: newMember.avatarUrl,
          admin: false,
          active: true,
        };
        const newMembers = projectMembers.push(memberData);
        dispatch(ProjectAction.updateProjectField("members", newMembers.toJS()));
        dispatch(
          ProjectAction.updateProject({
            ...project.toJS(),
            members: newMembers.toJS(),
          })
        );
      }
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  return (
    <div className="py-4 max-w-5xl">
      <div className="py-2 px-4 sm:px-0">
        <h3 className="text-lg font-semibold text-gray-900">{t("Members")}</h3>
        <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">
          {t("Manage pipeline members, set access levels")}
        </p>
      </div>
      <div className="py-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center">
            <div className="relative w-full mr-2">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
              <input
                type="text"
                placeholder={t("Find member")}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-600 text-sm font-medium placeholder-gray-500"
                onChange={(e) => {
                  const newVal = e.target.value || "";
                  setSearchText(newVal);
                }}
              />
            </div>
            <SelectField
              className="w-50 mr-2"
              value={roleList.find((option) => option.value === role)}
              onChange={(selectedOption) => setRole(selectedOption?.value)}
              options={roleList}
              placeholder={t("Role")}
              isClearable
              dropdownIndicator={<ChevronDownIcon className="w-3.5 h-3.5 text-gray-500" />}
            />
            <SelectField
              className="w-50"
              value={statusList.find((option) => option.value === status)}
              onChange={(selectedOption) => setStatus(selectedOption?.value)}
              options={statusList}
              placeholder={t("Status")}
              isClearable
              dropdownIndicator={<ChevronDownIcon className="w-3.5 h-3.5 text-gray-500" />}
            />
          </div>
          <div>
            <SelectField
              className="w-50"
              value={null}
              inputValue={addMemberSearch}
              onChange={(selectedOption) => {
                if (selectedOption?.value) {
                  setShowConfirm({
                    show: true,
                    title: t("Add Member"),
                    description: t("Are you sure you want to add this member?"),
                    type: "ADD_MEMBER",
                    data: selectedOption?.value,
                  });
                  // Clear input after selecting to reset search
                  setAddMemberSearch("");
                }
              }}
              onInputChange={(inputValue, { action }) => {
                if (action === "input-change") {
                  setAddMemberSearch(inputValue || "");
                }
              }}
              options={availableMembers}
              placeholder={t("Add Member")}
              isClearable
              dropdownIndicator={<PlusCircleIcon className="w-3.5 h-3.5 text-blue-600" />}
              formatOptionLabel={(option: any) => {
                return (
                  <div key={option.value} className="flex items-center gap-2">
                    <Avatar avatarUrl={option.avatarUrl} displayName={option.label} />
                    <span>{option.label}</span>
                  </div>
                );
              }}
            />
          </div>
        </div>
        <GridTable headers={memberHeaders} rows={filterData} pageSize={10} />
      </div>
      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={showConfirm.alertIcon || <OctagonAlertIcon />}
        dangerBtn={showConfirm.dangerBtn}
        onSubmit={handleSubmit}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </div>
  );
};

export default MemberSetting;
